import { devLog } from '../../utils/devLog';

import React, { useState } from 'react';
import { useAppContext } from '../../hooks/useAppContext';
import { useToast } from '../common/Toast';
import { deleteQuestion as deleteQuestionAPI } from '../../utils/api';
import Modal from '../common/Modal';

const QuestionItem = ({ question, index, onDelete }) => {
  const { deleteQuestion, jobs } = useAppContext();
  const { showSuccess, showError } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    
    try {
      // Use jobId directly if it's a roleId, otherwise find the job
      let roleId = question.jobId;
      
      // If jobId is not a roleId, try to find the job
      if (roleId && !roleId.includes('-')) {
        const job = jobs.find(j => j.id === question.jobId || j.roleId === question.jobId);
        if (job) {
          roleId = job.roleId || job.id;
        }
      }

      if (!roleId) {
        showError('Could not find the associated job role. Please refresh the page and try again.', 6000);
        setIsDeleting(false);
        return;
      }

      // Call API to delete question
      const result = await deleteQuestionAPI(roleId, question.id);

      if (result.success) {
        // Extract totalQuestions from response
        // Response structure: { status: "success", message: "...", data: { questionId: "...", totalQuestions: ... } }
        const totalQuestions = result.data?.data?.totalQuestions ?? result.data?.totalQuestions ?? 0;
        
        // Delete from local state if using context
        if (deleteQuestion) {
          deleteQuestion(question.id);
        }
        
        // Call refresh callback if provided (this will reload from API)
        if (onDelete) {
          // Small delay to ensure backend has processed the deletion
          setTimeout(() => {
            onDelete();
          }, 300);
        }
        
        // Show success message with remaining count
        showSuccess(`Question deleted successfully! Remaining questions: ${totalQuestions}`, 5000);
      } else {
        // Handle different error types
        if (result.notFound) {
          showError(`${result.error}. The question may have already been deleted. Refreshing the list...`, 6000);
          // Refresh the list if question not found
          if (onDelete) {
            setTimeout(() => {
              onDelete();
            }, 500);
          }
        } else {
          showError(result.error || 'Failed to delete question. Please try again.', 6000);
        }
      }
    } catch (error) {
      devLog.error('Error deleting question:', error);
      showError(`Failed to delete question: ${error.message || 'Please try again.'}`, 6000);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="question-item">
      <div style={{ flex: 1 }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '15px' 
        }}>
          <div style={{
            background: '#667eea',
            color: 'white',
            padding: '5px 15px',
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: 600
          }}>
            {question.type}
          </div>
          <div style={{ 
            display: 'flex', 
            gap: '15px', 
            fontSize: '0.9rem', 
            color: '#718096' 
          }}>
            <span>⏱️ {question.time}s</span>
            <span>⚖️ Weight: {question.weight}/10</span>
          </div>
        </div>
        <div style={{ 
          color: '#2d3748', 
          fontWeight: 600, 
          marginBottom: '10px', 
          lineHeight: 1.5 
        }}>
          Q{index + 1}: {question.text}
        </div>
      </div>
      <button 
        onClick={() => setShowDeleteModal(true)} 
        className="button button-danger"
        disabled={isDeleting}
      >
        {isDeleting ? '⏳ Deleting...' : '🗑️ Delete'}
      </button>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="🗑️ Delete Interview Question"
        message={
          <div>
            <p style={{ marginBottom: '15px', lineHeight: 1.6 }}>
              Are you sure you want to delete this question?
            </p>
            <div style={{
              background: '#f7fafc',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '15px',
              borderLeft: '4px solid #667eea'
            }}>
              <strong style={{ color: '#2d3748', display: 'block', marginBottom: '8px' }}>
                Question:
              </strong>
              <p style={{ color: '#4a5568', margin: 0, lineHeight: 1.6 }}>
                "{question.text.length > 200 ? question.text.substring(0, 200) + '...' : question.text}"
              </p>
            </div>
            <p style={{ color: '#e53e3e', fontWeight: 600, margin: 0 }}>
              ⚠️ This action is irreversible and cannot be undone.
            </p>
          </div>
        }
        confirmText="Delete Question"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default QuestionItem;
